import { BasketBillingType, BasketForm, BillingLanguage } from "../BasketService";
import { NaviButtons } from "../components/NaviButtons";
import { useTranslation } from "react-i18next";
import { Path, SubmitHandler, useForm } from "react-hook-form";
import { Input } from "../components/Input";
import { EmailInput } from "../components/EmailInput";
import { UserInputs } from "../components/UserInputs";
import { isNotEmptyString, withExceptionCapturing } from "../common";
import { phoneRegex, postalCodeRegex } from "../validation";

interface BasketFormInputs extends BasketForm {
	billingEmailConfirmation?: string;
	billingAddressSameAsAddress: boolean;
	emailConfirmation: string;
	user1EmailConfirmation: string;
	user2EmailConfirmation: string;
	user3EmailConfirmation: string;
	user4EmailConfirmation: string;
}

export const StepOrderInformation = ({
	returnUrl,
	initialBasketForm,
	onBack,
	onNext
}: {
	returnUrl: string;
	initialBasketForm: Partial<BasketForm>;
	onBack: () => void;
	onNext: (basketForm: BasketForm) => void;
}) => {
	const { t } = useTranslation();
	const defaultValues = {
		billingType: BasketBillingType.Online,
		billingLanguage: BillingLanguage.FI,
		billingAddressSameAsAddress: false,
		...initialBasketForm
	};

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting }
	} = useForm<BasketFormInputs>({
		defaultValues: defaultValues,
		mode: "onTouched"
	});

	const billingType = watch("billingType", defaultValues.billingType);
	const billingAddressSameAsAddress = watch("billingAddressSameAsAddress", defaultValues.billingAddressSameAsAddress);

	const isUniqueEmail = (emailPropName: Path<BasketFormInputs>, form: BasketFormInputs): boolean => {
		const email = form[emailPropName];
		if (!email) {
			return true;
		}

		const emailProps: Path<BasketFormInputs>[] = ["user1Email", "user2Email", "user3Email", "user4Email"];
		if (form.userAccountForSubscriber) {
			emailProps.push("email");
		}

		const emails = emailProps.map((e) => ({
			propName: e,
			value: form[e]
		}));

		return !emails.find((p) => p.propName !== emailPropName && email === p.value);
	};

	const hasAtLeastOneUser = (form: BasketFormInputs): string | undefined => {
		return form.userAccountForSubscriber ||
			isNotEmptyString(form.user1LastName) ||
			isNotEmptyString(form.user2LastName) ||
			isNotEmptyString(form.user3LastName) ||
			isNotEmptyString(form.user4LastName)
			? undefined
			: t("atLeastOneUser");
	};

	const onSubmit: SubmitHandler<BasketFormInputs> = (data) => {
		onNext({
			...data,
			...(data.billingAddressSameAsAddress && {
				billingStreetAddress: data.streetAddress,
				billingPostalCode: data.postalCode,
				billingCity: data.city
			})
		});
	};

	return (
		<form onSubmit={withExceptionCapturing(handleSubmit(onSubmit))}>
			<div className="row space-t">
				<div className="col-md-12 col-sm-12 col-xs-12">
					<div className="row">
						<div className="col-md-9 col-sm-12 col-xs-12">
							<h3>{t("orderInfoTitle")}</h3>
							<div className="row">
								<div className="col-md-4 col-sm-12 col-xs-12">
									<div className="form-group">
										<label>{t("companyName")}</label>
										<input {...register("companyName")} type="text" className="form-control" readOnly />
									</div>
								</div>
								<div className="col-md-4 col-sm-12 col-xs-12">
									<div className="form-group">
										<label>{t("companyId")}</label>
										<input {...register("businessId")} type="text" className="form-control" readOnly />
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-md-4 col-sm-12 col-xs-12">
									<Input
										register={register}
										name="streetAddress"
										errors={errors}
										label="streetAddress"
										placeholder="streetAddress"
										required
									/>
								</div>
								<div className="col-md-4 col-sm-12 col-xs-12">
									<Input
										register={register}
										name="postalCode"
										errors={errors}
										label="postalCode"
										placeholder="postalCode"
										required
										registerOptions={{
											pattern: {
												value: postalCodeRegex,
												message: t("invalidPostalCode")
											}
										}}
									/>
								</div>
								<div className="col-md-4 col-sm-12 col-xs-12">
									<Input register={register} name="city" errors={errors} label="city" placeholder="city" required />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="row space-t">
				<div className="col-md-12 col-sm-12 col-xs-12">
					<div className="row">
						<div className="col-md-9 col-sm-12 col-xs-12">
							<h4>{t("billingInfoTitle")}</h4>
							<div className="row">
								<div className="col-md-4 col-sm-12 col-xs-12">
									<div className="form-group">
										<div className="radio">
											<label>
												<input
													{...register("billingType", {
														required: t("requiredField"),
														deps: ["onlineBillingAddress", "billingEmail", "billingEmailConfirmation"]
													})}
													value={BasketBillingType.Online}
													type="radio"
												/>
												{t(`billingType${BasketBillingType.Online}`)}
											</label>
										</div>
										<div className="radio">
											<label>
												<input {...register("billingType")} value={BasketBillingType.Email} type="radio" />
												{t(`billingType${BasketBillingType.Email}`)}
											</label>
										</div>
										<div className="radio">
											<label>
												<input {...register("billingType")} value={BasketBillingType.Paper} type="radio" />
												{t(`billingType${BasketBillingType.Paper}`)}
											</label>
										</div>
									</div>
								</div>
								{billingType === BasketBillingType.Online && (
									<div className="col-md-8 col-sm-12 col-xs-12">
										<div className="row">
											<div className="col-md-6 col-sm-12 col-xs-12">
												<div className="form-group">
													<Input
														register={register}
														name="onlineBillingAddress"
														errors={errors}
														label="onlineBillingAddress"
														placeholder="onlineBillingAddress"
														required
													/>
												</div>
											</div>
										</div>
									</div>
								)}
								{billingType === BasketBillingType.Email && (
									<div className="col-md-8 col-sm-12 col-xs-12">
										<div className="row">
											<div className="col-md-6 col-sm-12 col-xs-12">
												<EmailInput
													register={register}
													name="billingEmail"
													errors={errors}
													label="email"
													placeholder="email"
													required
												/>
											</div>
											<div className="col-md-6 col-sm-12 col-xs-12">
												<EmailInput
													register={register}
													name="billingEmailConfirmation"
													errors={errors}
													label="emailRepeat"
													placeholder="emailRepeat"
													required
													registerOptions={{
														validate: {
															sameEmail: (_, values) =>
																values.billingEmail !== values.billingEmailConfirmation
																	? t("emailsDontMatch")
																	: undefined
														}
													}}
												/>
											</div>
										</div>
									</div>
								)}
							</div>
							<div className="row space-s-t">
								<div className="col-md-9 col-sm-12 col-xs-12">
									<label>{t("billingLanguage")} *</label>
									<div className="row">
										<div className="col-md-2 col-sm-12 col-xs-12">
											<div className="form-group">
												<div className="radio">
													<label>
														<input
															{...register("billingLanguage", { required: t("requiredField") })}
															value={BillingLanguage.FI}
															type="radio"
														/>
														{t(`billingLanguage${BillingLanguage.FI}`)}
													</label>
												</div>
											</div>
										</div>
										<div className="col-md-2 col-sm-12 col-xs-12">
											<div className="form-group">
												<div className="radio">
													<label>
														<input {...register("billingLanguage")} value={BillingLanguage.SV} type="radio" />
														{t(`billingLanguage${BillingLanguage.SV}`)}
													</label>
												</div>
											</div>
										</div>
										<div className="col-md-2 col-sm-12 col-xs-12">
											<div className="form-group">
												<div className="radio">
													<label>
														<input {...register("billingLanguage")} value={BillingLanguage.EN} type="radio" />
														{t(`billingLanguage${BillingLanguage.EN}`)}
													</label>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-md-12 col-sm-12 col-xs-12">
									<div className="form-group">
										<div className="checkbox">
											<label>
												<input {...register("billingAddressSameAsAddress")} type="checkbox" />
												{t("billingAddressSame")}
											</label>
										</div>
										<br />
									</div>
								</div>
							</div>
							{!billingAddressSameAsAddress && (
								<div className="row">
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="billingStreetAddress"
											errors={errors}
											label="billingStreetAddress"
											placeholder="billingStreetAddress"
											required
										/>
									</div>
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="billingPostalCode"
											errors={errors}
											label="postalCode"
											placeholder="postalCode"
											required
											registerOptions={{
												pattern: {
													value: postalCodeRegex,
													message: t("invalidPostalCode")
												}
											}}
										/>
									</div>
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="billingCity"
											errors={errors}
											label="city"
											placeholder="city"
											required
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="row space-t">
				<div className="col-md-9 col-sm-12 col-xs-12">
					<h4>{t("subscriberInfo")}</h4>
					<div className="row">
						<div className="col-md-4 col-sm-12 col-xs-12">
							<Input
								register={register}
								name="lastName"
								errors={errors}
								label="lastName"
								placeholder="lastName"
								required
								readOnly
							/>
						</div>
						<div className="col-md-4 col-sm-12 col-xs-12">
							<Input
								register={register}
								name="firstName"
								errors={errors}
								label="firstName"
								placeholder="firstName"
								required
								readOnly
							/>
						</div>
						<div className="col-md-4 col-sm-12 col-xs-12">
							<Input
								register={register}
								name="phone"
								errors={errors}
								inputType="tel"
								label="phone"
								placeholder="phone"
								required
								registerOptions={{
									pattern: {
										value: phoneRegex,
										message: t("invalidPhone")
									}
								}}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-4 col-sm-12 col-xs-12">
							<EmailInput
								register={register}
								name="email"
								errors={errors}
								label="email"
								placeholder="email"
								required
								registerOptions={{
									deps: ["emailConfirmation"]
								}}
							/>
						</div>
						<div className="col-md-4 col-sm-12 col-xs-12">
							<EmailInput
								register={register}
								name="emailConfirmation"
								errors={errors}
								label="emailRepeat"
								placeholder="emailRepeat"
								required
								registerOptions={{
									validate: {
										sameEmail: (_, values) =>
											values.email !== values.emailConfirmation ? t("emailsDontMatch") : undefined
									}
								}}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className="row space-t">
				<div className="col-md-12">
					<h3>{t("usersTitle")}</h3>
					<p>{t("usersInfo")}</p>
					<div className="col-md-12">
						<div className="row">
							<div className="col-md-12">
								<div className="checkbox">
									<label>
										<input
											{...register("userAccountForSubscriber", {
												deps: ["user1LastName", "user1Email", "user2Email", "user3Email", "user4Email"]
											})}
											type="checkbox"
										/>
										{t("createUserAccountForSubscriber")}
									</label>
								</div>
							</div>
						</div>
					</div>
					<div className="col-md-12">
						<UserInputs
							register={register}
							errors={errors}
							lastNameProp="user1LastName"
							firstNameProp="user1FirstName"
							emailProp="user1Email"
							emailConfirmationProp="user1EmailConfirmation"
							isUniqueEmail={isUniqueEmail}
							additionalValidate={(_, values) => hasAtLeastOneUser(values)}
						/>
						<UserInputs
							register={register}
							errors={errors}
							lastNameProp="user2LastName"
							firstNameProp="user2FirstName"
							emailProp="user2Email"
							emailConfirmationProp="user2EmailConfirmation"
							isUniqueEmail={isUniqueEmail}
						/>
						<UserInputs
							register={register}
							errors={errors}
							lastNameProp="user3LastName"
							firstNameProp="user3FirstName"
							emailProp="user3Email"
							emailConfirmationProp="user3EmailConfirmation"
							isUniqueEmail={isUniqueEmail}
						/>
						<UserInputs
							register={register}
							errors={errors}
							lastNameProp="user4LastName"
							firstNameProp="user4FirstName"
							emailProp="user4Email"
							emailConfirmationProp="user4EmailConfirmation"
							isUniqueEmail={isUniqueEmail}
						/>
					</div>
				</div>
			</div>
			<NaviButtons
				cancelVisible
				onCancel={() => (window.location.href = returnUrl)}
				backVisible
				onBack={onBack}
				nextDisabled={isSubmitting}
			/>
		</form>
	);
};
